import { secureImageUrl } from "services/utils/payslipTemplate";
import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
} from "@react-pdf/renderer";
import moment from "moment";
import Roboto from "../../../assets/fonts/Roboto/Roboto-Regular.ttf";
import RobotoBold from "../../../assets/fonts/Roboto/Roboto-Bold.ttf";

Font.register({
  family: "Roboto",
  fonts: [{ src: Roboto }, { src: RobotoBold, fontWeight: 600 }],
  format: "truetype",
});

// Without this, react-pdf's default hyphenation can split a long
// alphanumeric value like the establishment number mid-word (e.g. "CB-" /
// "SLM0070305000") at an arbitrary point instead of wrapping the whole
// word. Treating every "word" as unbreakable keeps values intact.
Font.registerHyphenationCallback((word) => [word]);

// PF establishment number is tied to the company's hosted domain, not to
// any per-employee payslip data, so it's looked up by hostname rather than
// read off payslipData (which the backend doesn't provide it on).
const ESTABLISHMENT_NO_BY_DOMAIN = {
  "vaibhav.organflow.app": "CBSLM0070305000",
  "vaibhav1.organflow.app": "CBSLM0070305000",
  "vigneswara.organflow.app": "CBSLM2875657000",
  "vigneswara1.organflow.app": "CBSLM2875657000",
  // Dev-only, so the establishment number can be checked while testing locally.
  localhost: "CBSLM0070305000",
  "127.0.0.1": "CBSLM0070305000",
};

const getEstablishmentNumber = () =>
  (typeof window !== "undefined" &&
    ESTABLISHMENT_NO_BY_DOMAIN[window.location.hostname]) ||
  null;

// Structural columns are sized by percentage width rather than `flex`:
// react-pdf takes each cell's border out of the space flex divides up, so two
// sections split the same way still land their rules a point apart. A
// percentage width is border-box and lands on the same x every time.
const BORDER = "1px solid #333";

const styles = StyleSheet.create({
  page: {
    padding: 24,
    fontFamily: "Roboto",
    color: "#222",
    fontSize: 9,
  },
  outer: {
    border: BORDER,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    padding: 6,
  },
  logoContainer: {
    width: 60,
    marginRight: 8,
  },
  logo: {
    width: 55,
    height: 30,
    objectFit: "contain",
  },
  companyInfo: {
    flex: 1,
    alignItems: "center",
  },
  companyName: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  companyAddress: {
    fontSize: 7.5,
    color: "#444",
    marginTop: 2,
    textAlign: "center",
  },
  periodRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderTop: BORDER,
    padding: 4,
  },
  titleBlock: {
    borderTop: BORDER,
    alignItems: "center",
    padding: 5,
  },
  title: {
    fontSize: 11,
    fontWeight: 600,
  },
  subtitle: {
    fontSize: 7.5,
    marginTop: 2,
  },
  infoRow: {
    flexDirection: "row",
    borderTop: BORDER,
  },
  infoCellLeft: {
    width: "50%",
    padding: 4,
    borderRight: BORDER,
  },
  infoCellRight: {
    width: "50%",
    padding: 4,
  },
  infoLine: {
    flexDirection: "row",
    marginBottom: 2,
  },
  infoLabel: {
    fontSize: 8,
    fontWeight: 600,
    width: 85,
  },
  infoValue: {
    fontSize: 8,
    flex: 1,
  },
  bodyRow: {
    flexDirection: "row",
    borderTop: BORDER,
  },
  earnDeductCol: {
    width: "66.6667%",
    flexDirection: "row",
    borderRight: BORDER,
  },
  tableHalf: {
    width: "50%",
  },
  tableHalfBorder: {
    width: "50%",
    borderRight: BORDER,
  },
  tableHeadRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderBottom: BORDER,
    padding: 3,
    backgroundColor: "#f2f2f2",
  },
  tableHeadText: {
    fontSize: 8,
    fontWeight: 600,
  },
  tableItemRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 3,
    borderBottom: "1px dotted #ccc",
  },
  tableItemText: {
    fontSize: 8,
  },
  tableTotalRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    padding: 3,
  },
  tableTotalText: {
    fontSize: 8,
    fontWeight: 600,
  },
  sideCol: {
    width: "33.3333%",
    padding: 4,
  },
  sideLine: {
    flexDirection: "row",
    marginBottom: 3,
  },
  sideLabel: {
    fontSize: 7.5,
    color: "#444",
    width: 90,
  },
  sideValue: {
    fontSize: 7.5,
    fontWeight: 600,
    flex: 1,
  },
  // Slightly smaller than sideValue so the long establishment number has
  // enough room to stay on one line within the space the fixed-width label
  // leaves it.
  sideValueCompact: {
    fontSize: 7,
    fontWeight: 600,
    flex: 1,
  },
  summarySection: {
    borderTop: BORDER,
    padding: 5,
  },
  summaryRow: {
    flexDirection: "row",
    marginBottom: 5,
  },
  summaryBox: {
    flex: 1,
    border: "1px solid #ccc",
    borderRadius: 3,
    padding: 5,
    marginRight: 5,
    alignItems: "center",
  },
  summaryBoxLast: {
    marginRight: 0,
  },
  summaryBoxLabel: {
    fontSize: 7.5,
    color: "#444",
    textAlign: "center",
  },
  summaryBoxValue: {
    fontSize: 9,
    fontWeight: 600,
    marginTop: 2,
  },
  netRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderTop: BORDER,
    padding: 5,
  },
  netLabel: {
    fontSize: 9,
    fontWeight: 600,
  },
  signRow: {
    flexDirection: "row",
    borderTop: BORDER,
  },
  signCell: {
    width: "33.3333%",
    padding: 8,
    borderRight: BORDER,
  },
  signCellLast: {
    width: "33.3333%",
    padding: 8,
  },
  signLabel: {
    fontSize: 7.5,
    color: "#444",
  },
  footer: {
    textAlign: "center",
    marginTop: 6,
    fontSize: 7.5,
    color: "#555",
  },
});

const formatAmount = (value) =>
  value === undefined || value === null || value === ""
    ? "N/A"
    : `Rs. ${value}`;

const getComponentName = (item) => item?.component_name ?? item?.name ?? "N/A";
const getComponentAmount = (item) =>
  item?.amount ?? item?.value ?? item?.total ?? "N/A";

const DEFAULT_BAND_LABEL_PATTERN = /^Default\s+(?:Band|Week\s*Off|Holiday)\b/i;

const normalizeComponentKey = (value) =>
  `${value || ""}`.toLowerCase().replace(/[^a-z0-9]/g, "");
const PF_KEYS = ["pf", "employeepf", "providentfund"];
const ESI_KEYS = ["esi", "employeeesi", "employeestateinsurance"];
const isStatutoryDeduction = (item) => {
  const key = normalizeComponentKey(getComponentName(item));
  return PF_KEYS.includes(key) || ESI_KEYS.includes(key);
};
// The employee may or may not have a PF/ESI deduction component in this pay
// run, but the wage slip always prints both lines (falling back to 0) so
// employees can see their PF/ESI status at a glance rather than the row
// silently disappearing when there's nothing withheld.
const extractStatutoryAmount = (rows, keys) => {
  const match = rows.find((item) =>
    keys.includes(normalizeComponentKey(getComponentName(item))),
  );
  return match ? toNumber(getComponentAmount(match)) : 0;
};

const toNumber = (value) => {
  if (value === undefined || value === null || value === "") {
    return 0;
  }
  const parsed = parseFloat(`${value}`.replace(/,/g, ""));
  return Number.isNaN(parsed) ? 0 : parsed;
};

// The payslip payload names the same list differently depending on the API it
// comes from (deduction / deductions, other_deduction / additional_deduction),
// so take the first one that actually carries rows rather than concatenating
// every alias and printing the components twice.
const pickSection = (...aliases) =>
  aliases.find((alias) => Array.isArray(alias) && alias.length)?.filter(Boolean) ??
  [];

// Same alias problem as pickSection, but for a single count — take the first
// value that actually parses as a number rather than the first one present.
const pickNumber = (...values) => {
  for (const value of values) {
    if (value === undefined || value === null || value === "") continue;
    const parsed = parseFloat(`${value}`.replace(/,/g, ""));
    if (!Number.isNaN(parsed)) return parsed;
  }
  return 0;
};

const sumTotals = (...values) =>
  values.reduce((sum, value) => sum + toNumber(value), 0);

const roundToTwoDecimals = (value) =>
  Math.round((value + Number.EPSILON) * 100) / 100;

// An Hours-basis payrun doesn't populate net_payable_shift, so the payable
// day count comes from total_payable_days instead for that basis.
const getNetPayableShifts = (payslipData) => {
  const value =
    payslipData?.payrun_basis === "Hours"
      ? payslipData?.total_payable_days
      : payslipData?.net_payable_shift;

  if (value === undefined || value === null || value === "") return "N/A";
  return roundToTwoDecimals(toNumber(value)).toFixed(2);
};

// Preview-only: real payslips always list every earning component, but the
// template preview folds zero/blank base-earning rows, Leave Salary, and all
// OT/other-earning rows into one "Others" line so the sample stays short.
const PREVIEW_ALWAYS_COLLAPSED_EARNINGS = ["leave salary"];
const collapseMinorEarnings = (earnings, otherEarnings) => {
  const isCollapsed = (item) =>
    toNumber(getComponentAmount(item)) === 0 ||
    PREVIEW_ALWAYS_COLLAPSED_EARNINGS.includes(
      `${getComponentName(item)}`.trim().toLowerCase(),
    );
  const significant = earnings.filter((item) => !isCollapsed(item));
  const collapsedAmount = roundToTwoDecimals(
    sumTotals(
      ...earnings.filter(isCollapsed).map(getComponentAmount),
      ...otherEarnings.map(getComponentAmount),
    ),
  );

  return collapsedAmount > 0
    ? [...significant, { component_name: "Others", amount: collapsedAmount }]
    : significant;
};

// Preview-only, mirrors collapseMinorEarnings: keeps the statutory/major
// deduction lines and folds the rest (plus all other-deduction rows) into
// one "Others" line so the sample stays short.
const PREVIEW_KEPT_DEDUCTIONS = ["pf", "esi"];
const collapseMinorDeductions = (deductions, otherDeductions) => {
  const isKept = (item) =>
    PREVIEW_KEPT_DEDUCTIONS.includes(
      `${getComponentName(item)}`.trim().toLowerCase(),
    );
  const significant = deductions.filter(isKept);
  const collapsedAmount = roundToTwoDecimals(
    sumTotals(
      ...deductions.filter((item) => !isKept(item)).map(getComponentAmount),
      ...otherDeductions.map(getComponentAmount),
    ),
  );

  return collapsedAmount > 0
    ? [...significant, { component_name: "Others", amount: collapsedAmount }]
    : significant;
};

const formatTextValue = (value) =>
  value === undefined || value === null || value === "" ? "N/A" : value;

const formatDateValue = (value) => {
  if (!value || value === "N/A") {
    return "N/A";
  }

  const parsedDate = moment(
    value,
    ["DD-MM-YYYY", "YYYY-MM-DD", "DD/MM/YYYY", "YYYY/MM/DD", moment.ISO_8601],
    true,
  );

  return parsedDate.isValid() ? parsedDate.format("DD-MM-YYYY") : value;
};

const formatAmountInWords = (value) => {
  if (value === undefined || value === null || value === "") {
    return "";
  }

  const words = `${value}`.trim().replace(/^rupees\s+/i, "");
  return words ? `(Rupees ${words})` : "";
};

const renderColumn = (title, data, total) => (
  <View style={styles.tableHalfBorder}>
    <View style={styles.tableHeadRow}>
      <Text style={styles.tableHeadText}>{title}</Text>
      <Text style={styles.tableHeadText}>Amount</Text>
    </View>
    {Array.isArray(data) &&
      data.map((item, index) => (
        <View style={styles.tableItemRow} key={index}>
          <Text style={styles.tableItemText}>{getComponentName(item)}</Text>
          <Text style={styles.tableItemText}>
            Rs. {getComponentAmount(item)}
          </Text>
        </View>
      ))}
    <View style={styles.tableTotalRow}>
      <Text style={styles.tableTotalText}>Total {title}</Text>
      <Text style={styles.tableTotalText}>
        Rs. {Math.round((toNumber(total) + Number.EPSILON) * 100) / 100}
      </Text>
    </View>
  </View>
);

export const WageSlipPage = ({ payslipData, isPreview = false }) => {
  if (!payslipData) {
    return null;
  }

  const departmentName = formatTextValue(payslipData?.department_name);
  const fatherHusbandName = formatTextValue(payslipData?.parents_name);
  // Real payslips show N/A when date_of_join is genuinely missing; the
  // preview sample doesn't carry that field at all, so it falls back to
  // today's date instead of printing N/A.
  const dateOfEntry = payslipData?.date_of_join
    ? formatDateValue(payslipData.date_of_join)
    : isPreview
      ? moment().format("DD-MM-YYYY")
      : "N/A";
  const pfNumber = formatTextValue(payslipData?.pf_no);
  const establishmentNo = payslipData?.establishment_no || getEstablishmentNumber();
  const esiNumber = formatTextValue(payslipData?.esi_no);
  const payPeriodStart = formatDateValue(payslipData?.start_date);
  const payPeriodEnd = formatDateValue(payslipData?.end_date);
  const payPeriod =
    payPeriodStart === "N/A" && payPeriodEnd === "N/A"
      ? "N/A"
      : `${payPeriodStart} to ${payPeriodEnd}`;
  const netPayInWords = formatAmountInWords(payslipData?.net_pay_in_words);

  // Manually added other earnings / other deductions belong to the same pay
  // run, so they print inside the Earnings and Deductions columns instead of
  // being dropped from the wage slip.
  const baseEarnings = pickSection(payslipData?.earnings);
  // An Other Earnings row not mapped to a configured OT band shows the
  // backend's raw "Default <band>" placeholder: "Default Band" for plain
  // overtime, "Default Week Off" / "Default Holiday" for those categories
  // (e.g. "Default Band Overtime", "Default Week Off Weekoff Attendance").
  // Every one of them is the same unconfigured/factor-1 band and reads as
  // "1 OT".
  const otherEarnings = pickSection(payslipData?.other_earnings).map((item) => {
    const name = getComponentName(item);
    return typeof name === "string" && DEFAULT_BAND_LABEL_PATTERN.test(name)
      ? {
          ...item,
          component_name: name
            .replace(DEFAULT_BAND_LABEL_PATTERN, "1 OT")
            .replace(/\s+/g, " ")
            .trim(),
        }
      : item;
  });
  const baseDeductions = pickSection(payslipData?.deduction, payslipData?.deductions);
  const otherDeductions = pickSection(
    payslipData?.other_deduction,
    payslipData?.other_deductions,
    payslipData?.additional_deduction,
  );

  // Real payslips always show an "Others" line in both columns, even when
  // there's nothing to fold into it, so the layout stays consistent across
  // employees instead of the row appearing/disappearing per payslip.
  const hasOthersRow = (rows) =>
    rows.some(
      (item) => `${getComponentName(item)}`.trim().toLowerCase() === "others",
    );
  const earningRows = isPreview
    ? collapseMinorEarnings(baseEarnings, otherEarnings)
    : hasOthersRow(otherEarnings)
      ? [...baseEarnings, ...otherEarnings]
      : [...baseEarnings, ...otherEarnings, { component_name: "Others", amount: 0 }];

  const combinedDeductions = [...baseDeductions, ...otherDeductions];
  const nonStatutoryDeductions = combinedDeductions.filter(
    (item) => !isStatutoryDeduction(item),
  );
  const deductionRows = isPreview
    ? collapseMinorDeductions(baseDeductions, otherDeductions)
    : [
        {
          component_name: "Employee PF",
          amount: extractStatutoryAmount(combinedDeductions, PF_KEYS),
        },
        {
          component_name: "Employee ESI",
          amount: extractStatutoryAmount(combinedDeductions, ESI_KEYS),
        },
        ...nonStatutoryDeductions,
        ...(hasOthersRow(nonStatutoryDeductions)
          ? []
          : [{ component_name: "Others", amount: 0 }]),
      ];
  const totalEarnings = sumTotals(
    payslipData?.total_earnings,
    payslipData?.total_other_earnings,
  );
  const totalDeductions = sumTotals(
    payslipData?.total_deductions,
    payslipData?.total_other_deductions ??
      payslipData?.total_additional_deductions,
  );

  // Each reads a chain of plausible keys (confirmed against a live payslip
  // response: total_week_off_days, total_holidays, total_absent_days) and
  // falls back to 0. Same Hours-basis handling as getNetPayableShifts: total_payable_days
  // instead of net_payable_shift when the payrun is basis Hours.
  const workedDays = roundToTwoDecimals(
    pickNumber(
      payslipData?.payrun_basis === "Hours"
        ? payslipData?.total_payable_days
        : payslipData?.net_payable_shift,
      payslipData?.days_worked,
      payslipData?.number_of_days_worked,
    ),
  ).toFixed(2);
  const weekoffDays = pickNumber(
    payslipData?.week_off_days,
    payslipData?.weekoff_days,
    payslipData?.week_off_count,
    payslipData?.weekly_off_days,
    payslipData?.total_week_off_days,
  );
  const nhFhDays = pickNumber(
    payslipData?.nh_fh_days,
    payslipData?.holidays,
    payslipData?.holiday_days,
    payslipData?.total_holidays,
  );
  const absentDays = pickNumber(
    payslipData?.absent_days,
    payslipData?.lop_days,
    payslipData?.lop_days_count,
    payslipData?.loss_of_pay_days,
    payslipData?.total_absent_days,
  );

  const attendanceSummary = [
    { label: "Worked Days", value: workedDays },
    { label: "Weekoff Days", value: weekoffDays },
    { label: "NH / FH Days", value: nhFhDays },
    { label: "Absent Days", value: absentDays },
  ];

  const payTotalsSummary = [
    { label: "Total Earnings", value: formatAmount(totalEarnings) },
    { label: "Total Deductions", value: formatAmount(totalDeductions) },
    {
      label: "Net Salary Payable",
      value: formatAmount(payslipData?.net_pay),
    },
  ];

  return (
    <Page size="A4" style={styles.page}>
      <View style={styles.outer}>
        <View style={styles.header}>
          {payslipData?.company_logo_url ? (
            <View style={styles.logoContainer}>
              <Image style={styles.logo} src={secureImageUrl(payslipData.company_logo_url)} />
            </View>
          ) : null}
          <View style={styles.companyInfo}>
            <Text style={styles.companyName}>
              {payslipData?.company_name || "Organflow"}
            </Text>
            {payslipData?.company_address && (
              <Text style={styles.companyAddress}>
                {payslipData?.company_address}
              </Text>
            )}
          </View>
        </View>

        <View style={styles.periodRow}>
          <Text style={{ fontSize: 8, fontWeight: 600 }}>
            Wage Month: {payslipData?.pay_schedule_name || "-"}
          </Text>
          <Text style={{ fontSize: 8, fontWeight: 600 }}>
            Wage Period: {payPeriod}
          </Text>
        </View>

        <View style={styles.titleBlock}>
          <Text style={styles.title}>WAGE SLIP</Text>
          <Text style={styles.subtitle}>
            (Under Rule 27(2) Of the minimum wages Chennai rules 1953)
          </Text>
        </View>

        <View style={styles.infoRow}>
          <View style={styles.infoCellLeft}>
            <View style={styles.infoLine}>
              <Text style={styles.infoLabel}>Employee Code</Text>
              <Text style={styles.infoValue}>
                : {payslipData?.employee_id ?? "N/A"}
              </Text>
            </View>
            <View style={styles.infoLine}>
              <Text style={styles.infoLabel}>Employee Name</Text>
              <Text style={styles.infoValue}>
                : {payslipData?.user_name ?? "N/A"}
              </Text>
            </View>
          </View>
          <View style={styles.infoCellRight}>
            <View style={styles.infoLine}>
              <Text style={styles.infoLabel}>Designation</Text>
              <Text style={styles.infoValue}>
                : {payslipData?.user_designation ?? "N/A"}
              </Text>
            </View>
            <View style={styles.infoLine}>
              <Text style={styles.infoLabel}>Department</Text>
              <Text style={styles.infoValue}>: {departmentName}</Text>
            </View>
          </View>
        </View>

        <View style={styles.bodyRow}>
          <View style={styles.earnDeductCol}>
            {renderColumn("Earnings", earningRows, totalEarnings)}
            {renderColumn("Deductions", deductionRows, totalDeductions)}
          </View>

          <View style={styles.sideCol}>
            <View style={styles.sideLine}>
              <Text style={styles.sideLabel}>Father's/Husband's Name</Text>
              <Text style={styles.sideValue}>: {fatherHusbandName}</Text>
            </View>
            <View style={styles.sideLine}>
              <Text style={styles.sideLabel}>Date of Join</Text>
              <Text style={styles.sideValue}>: {dateOfEntry}</Text>
            </View>
            <View style={styles.sideLine}>
              <Text style={styles.sideLabel}>Net Payable Shifts</Text>
              <Text style={styles.sideValue}>
                : {getNetPayableShifts(payslipData)}
              </Text>
            </View>
            <View style={styles.sideLine}>
              <Text style={styles.sideLabel}>Monthly Salary</Text>
              <Text style={styles.sideValue}>
                : {payslipData?.monthly_salary ?? "N/A"}
              </Text>
            </View>
            {establishmentNo ? (
              <View style={styles.sideLine}>
                <Text style={styles.sideLabel}>Establishment No</Text>
                <Text style={styles.sideValueCompact}>: {establishmentNo}</Text>
              </View>
            ) : null}
            <View style={styles.sideLine}>
              <Text style={styles.sideLabel}>P.F No</Text>
              <Text style={styles.sideValue}>: {pfNumber}</Text>
            </View>
            <View style={styles.sideLine}>
              <Text style={styles.sideLabel}>E.S.I No</Text>
              <Text style={styles.sideValue}>: {esiNumber}</Text>
            </View>
          </View>
        </View>

        <View style={styles.summarySection}>
          <View style={styles.summaryRow}>
            {attendanceSummary.map((item, index) => (
              <View
                key={item.label}
                style={
                  index === attendanceSummary.length - 1
                    ? [styles.summaryBox, styles.summaryBoxLast]
                    : styles.summaryBox
                }
              >
                <Text style={styles.summaryBoxLabel}>{item.label}</Text>
                <Text style={styles.summaryBoxValue}>{item.value}</Text>
              </View>
            ))}
          </View>
          <View style={styles.summaryRow}>
            {payTotalsSummary.map((item, index) => (
              <View
                key={item.label}
                style={
                  index === payTotalsSummary.length - 1
                    ? [styles.summaryBox, styles.summaryBoxLast]
                    : styles.summaryBox
                }
              >
                <Text style={styles.summaryBoxLabel}>{item.label}</Text>
                {item.secondaryLabel ? (
                  <Text style={styles.summaryBoxLabel}>{item.secondaryLabel}</Text>
                ) : null}
                <Text style={styles.summaryBoxValue}>{item.value}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={styles.netRow}>
          <Text style={styles.netLabel}>
            Net Amount Paid: {formatAmount(payslipData?.net_pay)}
            {netPayInWords ? ` ${netPayInWords}` : ""}
          </Text>
        </View>

        <View style={styles.signRow}>
          <View style={styles.signCell}>
            <Text style={styles.signLabel}>Employee's Signature</Text>
          </View>
          <View style={styles.signCell}>
            <Text style={styles.signLabel}>
              Date of Payment: {payslipData?.pay_date ?? "N/A"}
            </Text>
          </View>
          <View style={styles.signCellLast}>
            <Text style={styles.signLabel}>Authorised Signatory</Text>
          </View>
        </View>
      </View>

      <Text style={styles.footer}>
        This is a system generated payslip. Signature is not required.
      </Text>
    </Page>
  );
};

const WageSlipData = ({ payslipData, isPreview = false }) => (
  <Document
    title={`${
      payslipData?.user_name ? payslipData?.user_name + " " : ""
    }${payslipData?.pay_schedule_name || "wage-slip"}`}
  >
    <WageSlipPage payslipData={payslipData} isPreview={isPreview} />
  </Document>
);

export const BulkWageSlipData = ({ employeesPdfData = [] }) => (
  <Document>
    {employeesPdfData
      .filter((emp) => emp?.payslipData)
      .map((employee, index) => (
        <WageSlipPage key={index} payslipData={employee.payslipData} />
      ))}
  </Document>
);

export default WageSlipData;
